import type { DropdownItem, DropdownOption } from "@jobteaser/spark/types/Dropdown"

/**
 * Finds a DropdownOption by its value from an array of DropdownItems.
 *
 * This utility function searches through different dropdown patterns:
 * - Simple options array
 * - Sections with options
 * - Mixed arrays with sections and options
 * - Dividers and other item types
 *
 * @param items - Array of dropdown items to search through
 * @param targetValue - The value to search for
 * @returns The matching DropdownOption or undefined if not found
 */
export const findOptionByValue = (items: DropdownItem[], targetValue: string): DropdownOption | undefined => {
  for (const item of items) {
    if (item.type === "option" && item.value === targetValue) {
      return item
    }

    if (item.type === "section" && item.options) {
      const option = item.options.find(opt => opt.value === targetValue)
      if (option) return option
    }
  }

  return undefined
}

/**
 * Converts an array of string values to an array of DropdownOption objects
 * by finding matching options in the provided dropdown items.
 *
 * This utility function searches through different dropdown patterns:
 * - Simple options array
 * - Sections with options
 * - Mixed arrays with sections and options
 * - Dividers and other item types
 *
 * @param items - Array of dropdown items to search through
 * @param targetValues - Array of string values to convert
 * @returns Array of DropdownOption objects that match the provided values
 */
export const findOptionsByValues = (items: DropdownItem[], targetValues: string[]): DropdownOption[] => {
  const options: DropdownOption[] = []

  for (const targetValue of targetValues) {
    const option = findOptionByValue(items, targetValue)
    if (option) {
      options.push(option)
    }
  }

  return options
}
